import { FilterOutlined } from '@ant-design/icons';
import { useCallback, useMemo, useState } from 'react';
import { Button, Col, Dropdown, Grid, Layout, Pagination, Row, Spin } from 'antd';
import useLocationFilter from '@/hooks/useLocationFilter';

import SponsorCard from '@/components/sponsors/SponsorCard';
import SponsorFiltersRow from '@/components/sponsors/SponsorFiltersRow';
import useLevel from '@/context/useLevel';
import useAssets from '@/hooks/useAssets';
import usePagedFilterState from '@/hooks/common/usePagedFilterState';
import { useSponsors } from '@/hooks/sponsor/useSponsorPageData';
import { renderResponsivePaginationItem } from '@/utils/paginationUtils';
import { INITIAL_SPONSOR_FILTER, SPONSOR_MOBILE_BREAKPOINT } from '@/utils/sponsorPage/constants';
import useTranslation from '@/hooks/useTranslation';


export default function SponsorSection() {
    const screens = Grid.useBreakpoint();
    const { getUrl } = useAssets();
    const { t } = useTranslation();
    const fallbackImage = getUrl?.('images/logo.png');
    const levelProps = useLevel();
    const { filter, setFilter, setPagination } = usePagedFilterState(INITIAL_SPONSOR_FILTER);
    const [searchText, setSearchText] = useState('');
    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
    const { data: sponsorsData = {}, isFetching } = useSponsors(filter);

    const isDesktop = Boolean(screens.lg);
    const isMobile = !screens.md;

    const { countryOptions } = useLocationFilter();

    const translatedLevelProps = useMemo(() => ({
        ...levelProps,
        options: (levelProps?.options || []).map((opt) => ({
            ...opt,
            label: t(`level.${opt.name?.toLowerCase()}`),
        })),
    }), [levelProps, t]);

    const renderPaginationItem = useMemo(
        () => renderResponsivePaginationItem(SPONSOR_MOBILE_BREAKPOINT, t('pagination.prev'), t('pagination.next')),
        [t]
    );

    const handlePageChange = useCallback((page, limit) => {
        setPagination(page, limit);
    }, [setPagination]);

    return (
        <section style={{ paddingBlock: 5, paddingInline: 5, backgroundColor: '#f5f5f5' }}>
            <Spin spinning={isFetching}>
                <Layout style={{ width: '100%', minHeight: '350px' }}>
                    <Layout.Header>
                        {isDesktop ? (
                            <SponsorFiltersRow
                                title={t('sponsor_section.title')}
                                setFilter={setFilter}
                                searchText={searchText}
                                setSearchText={setSearchText}
                                levelProps={translatedLevelProps}
                                countryOptions={countryOptions}
                            />
                        ) : (
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <h4 style={{ margin: 0 }}>{t('sponsor_section.title')}</h4>
                                </Col>
                                <Col>
                                    <Dropdown
                                        trigger={['click']}
                                        placement="bottomLeft"
                                        open={isFilterDropdownOpen}
                                        onOpenChange={setIsFilterDropdownOpen}
                                        popupRender={() => (
                                            <div
                                                style={{
                                                    width: 'min(92vw, 520px)',
                                                    background: '#fff',
                                                    borderRadius: 8,
                                                    padding: 12,
                                                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)',
                                                }}
                                            >
                                                <SponsorFiltersRow
                                                    isFull
                                                    showTitle={false}
                                                    title={t('sponsor_section.title')}
                                                    setFilter={setFilter}
                                                    searchText={searchText}
                                                    setSearchText={setSearchText}
                                                    levelProps={translatedLevelProps}
                                                    countryOptions={countryOptions}
                                                />
                                            </div>
                                        )}
                                    >
                                        <Button size="large" icon={<FilterOutlined />}>
                                            {t('sponsor_section.filter_btn')}
                                        </Button>
                                    </Dropdown>
                                </Col>
                            </Row>
                        )}
                    </Layout.Header>

                    <Layout.Content style={{ paddingBlock: 10 }}>
                        <Row gutter={[16, 16]} justify="center">
                            {(sponsorsData?.data || []).map((sponsor) => (
                                <Col key={sponsor.id} xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                    <SponsorCard sponsor={sponsor} isFetching={isFetching} fallbackImage={fallbackImage} />
                                </Col>
                            ))}
                        </Row>

                        <Row gutter={[8, 8]} justify="center" style={{ marginTop: 20 }}>
                            <Col>
                                <Pagination
                                    hideOnSinglePage
                                    total={sponsorsData?.total || 0}
                                    current={sponsorsData?.current_page || 1}
                                    pageSize={sponsorsData?.per_page || 10}
                                    onChange={handlePageChange}
                                    itemRender={renderPaginationItem}
                                    showSizeChanger={!isMobile}
                                />
                            </Col>
                        </Row>
                    </Layout.Content>
                </Layout>
            </Spin>
        </section>
    );
}

